<template>
  <div>
    <content-loading v-if="isInitializing" />
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div class="d-flex" style="margin-bottom: 1.70rem;" @click="goBack()">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;">
            <path
              d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
              stroke="#FF7C48"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <div class="d-flex align-items-center" style="margin-left: 1.25rem; gap: 2.34rem;">
            <div>
              <h2 class="mb-1">{{ $t("firm.audit.detail-title") }}</h2>
              <h5 v-if="firm" class="text-muted mb-0">{{ firm.name | translate }}</h5>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center">
          <b-button variant="light" size="sm" class="mr-2" @click="refresh">
            <i class="bx bx-refresh"></i>
            {{ $t("common.refresh") }}
          </b-button>

          <b-button variant="outline-primary" size="sm" class="mr-2" @click="$bvModal.show('create-audit-modal')" :disabled="isAuditsLoading">
            <i class="bx bx-plus"></i>
            {{ $t("firm.audit.start-new") }}
          </b-button>
        </div>
      </div>
      <div v-if="firm" class="mb-4">
        <span class="mr-4">
          <i class="mdi mdi-help text-main"></i>
          <strong>{{ $t("firm.support-service") }}:</strong>
          {{ firm.supportService ? $t("common.yes") : $t("common.no") }}
        </span>
        <span class="mr-4">
          <i class="mdi mdi-server text-main"></i>
          <strong>{{ $t("firm.virtual-server") }}:</strong>
          {{ firm.virtualServer ? $t("common.yes") : $t("common.no") }}
        </span>
        <span class="mr-4">
          <strong>{{ $t("firm.status.text") }}:</strong>
          <firm-status :status="firmStatus" />
        </span>
      </div>
      <b-modal id="create-audit-modal" :title="$t('firm.audit.start-new') || 'Yeni denetim başlat'" hide-footer>
        <div>
          <b-form-group :label="$t('firm.audit.name') || 'Denetim adı'">
            <sweet-input v-model="newAuditName" :isValid="!$v.newAuditName.$error" @input="$v.newAuditName.$touch()" type="text" :placeholder="$t('firm.audit.name')" />
          </b-form-group>
          <div class="d-flex justify-content-end mt-3">
            <b-button variant="secondary" size="sm" class="mr-2" @click="$bvModal.hide('create-audit-modal')">{{ $t("common.cancel") }}</b-button>
            <b-button variant="primary" size="sm" :disabled="isCreatingAudit" @click="createAudit">
              <b-spinner v-if="isCreatingAudit" small type="border" class="mr-2" />
              {{ $t("common.save") }}
            </b-button>
          </div>
        </div>
      </b-modal>
      <b-modal id="edit-audit-modal" :title="$t('common.edit')" hide-footer>
        <div>
          <b-form-group :label="$t('firm.audit.name') || 'Denetim adı'">
            <sweet-input v-model="editAuditName" :isValid="!$v.editAuditName.$error" @input="$v.editAuditName.$touch()" type="text" :placeholder="$t('firm.audit.name')" />
          </b-form-group>
          <b-form-group :label="$t('firm.audit.status.text') || 'Durum'">
            <b-form-select v-model="editAuditStatus" :options="statusOptions" />
          </b-form-group>
          <div class="d-flex justify-content-end mt-3">
            <b-button variant="secondary" size="sm" class="mr-2" @click="$bvModal.hide('edit-audit-modal')">{{ $t("common.cancel") }}</b-button>
            <b-button variant="primary" size="sm" :disabled="isUpdatingAudit || $v.editAuditName.$error || $v.editAuditStatus.$error" @click="updateAudit">
              <b-spinner v-if="isUpdatingAudit" small type="border" class="mr-2" />
              {{ $t("common.save") }}
            </b-button>
          </div>
        </div>
      </b-modal>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="mb-0">{{ $t("firm.audit.list-title") }}</h5>
      </div>
      <b-tabs v-model="tabIndex" justified nav-class="nav-tabs-custom">
        <b-tab active>
          <template v-slot:title>
            <h6 class="m-1">{{ $t("firm.audit-status.active") }}</h6>
          </template>

          <div class="search--input-container mb-3 w-27">
            <i class="search--icon iconoir-search"></i>
            <input class="search--input" type="text" v-model="nameFilter" :placeholder="$t('common.search')" />
          </div>
          <div class="audit-section mb-4">
            <content-loading v-if="isAuditsLoading" />
            <empty v-else-if="!audits.length">
              <h6 class="text-center mb-0">{{ $t("firm.audit.no-audit") }}</h6>
            </empty>
            <div v-else class="audit-grid">
              <div v-for="audit in audits" :key="audit._id" class="audit-card" :class="{ active: audit._id === (selectedAudit && selectedAudit._id) }" @click="selectAudit(audit)">
                <div class="d-flex justify-content-end">
                  <b-button variant="outline-secondary" size="sm" class="mr-2" @click="openEditModal(audit)" :disabled="!selectedAudit || isAuditsLoading">
                    <i class="bx bx-edit-alt"></i>
                    {{ $t("common.edit") }}
                  </b-button>
                  <b-button variant="outline-danger" size="sm" class="mr-2" @click="confirmDeleteAudit(audit)" :disabled="!selectedAudit || isAuditsLoading || isDeletingAudit">
                    <b-spinner v-if="isDeletingAudit" small type="border" class="mr-1" />
                    <template v-else>
                      <i class="bx bx-trash"></i>
                    </template>
                    {{ $t("common.delete") }}
                  </b-button>
                </div>
                <div class="audit-name">{{ audit.name | translate }}</div>
                <div class="audit-meta text-muted mb-1 mt-4">
                  <span>{{ $t("firm.audit.status.text") }}:</span>
                  <strong>{{ formatAuditStatus(audit.status) }}</strong>
                </div>
                <div class="audit-meta text-muted">
                  <span>{{ $t("firm.audit.audit-start") }}:</span>
                  <span>{{ audit.startedAt | date(true) }}</span>
                </div>
                <div class="audit-meta text-muted">
                  <span>{{ $t("common.created-at") }}:</span>
                  <span>{{ audit.createdAt | date(true) }}</span>
                </div>
                <div class="audit-status-list">
                  <div v-for="entry in questionReviewStatusEntries(audit)" :key="entry.status" class="audit-status-chip">
                    <span class="text-muted">{{ formatQuestionReviewStatus(entry.status) }}:</span>
                    <strong>{{ entry.count }}</strong>
                  </div>
                </div>

                <div class="mt-3 d-flex justify-content-center ">
                  <b-button variant="outline-main" size="sm" class="w-50 mr-1" @click="goToAuditQuestions(audit)">
                    {{ $t("firm.view-question-set") }}
                  </b-button>
                  <b-button variant="outline-primary" size="sm" class="w-50" @click="goToFirmPolicy(audit)"> {{ $t("audit.view-policies") }} </b-button>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

        <b-tab>
          <template v-slot:title>
            <h6 class="m-1">{{ $t("firm.audit-status.completed") }}</h6>
          </template>

          <div class="search--input-container mb-3 w-25">
            <i class="search--icon iconoir-search"></i>
            <input class="search--input" type="text" v-model="nameFilter" :placeholder="$t('question-set.search')" />
          </div>
          <div class="audit-section mb-4">
            <content-loading v-if="isAuditsLoading" />
            <empty v-else-if="!audits.length">
              <h6 class="text-center mb-0">{{ $t("firm.audit.no-audit") }}</h6>
            </empty>
            <div v-else class="audit-grid">
              <div v-for="audit in audits" :key="audit._id" class="audit-card" :class="{ active: audit._id === (selectedAudit && selectedAudit._id) }" @click="selectAudit(audit)">
                <div class="d-flex justify-content-end">
                  <b-button variant="outline-secondary" size="sm" class="mr-2" @click="openEditModal(audit)" :disabled="!selectedAudit || isAuditsLoading">
                    <i class="bx bx-edit-alt"></i>
                    {{ $t("common.edit") }}
                  </b-button>
                  <b-button variant="outline-danger" size="sm" class="mr-2" @click="confirmDeleteAudit(audit)" :disabled="!selectedAudit || isAuditsLoading || isDeletingAudit">
                    <b-spinner v-if="isDeletingAudit" small type="border" class="mr-1" />
                    <template v-else>
                      <i class="bx bx-trash"></i>
                    </template>
                    {{ $t("common.delete") }}
                  </b-button>
                </div>
                <div class="audit-name">{{ audit.name | translate }}</div>
                <div class="audit-meta text-muted mb-1 mt-4">
                  <span>{{ $t("firm.audit.status.text") }}:</span>
                  <strong>{{ formatAuditStatus(audit.status) }}</strong>
                </div>
                <div class="audit-meta text-muted">
                  <span>{{ $t("firm.audit.audit-start") }}:</span>
                  <span>{{ audit.startedAt | date(true) }}</span>
                </div>
                <div class="audit-meta text-muted">
                  <span>{{ $t("common.created-at") }}:</span>
                  <span>{{ audit.createdAt | date(true) }}</span>
                </div>
                <div v-if="audit.status == 'COMPLETED'" class="audit-meta text-muted">
                  <span>{{ $t("firm.audit.completed-at") }}:</span>
                  <span>{{ audit.completedAt | date(true) }}</span>
                </div>

                <div v-if="questionReviewStatusEntries(audit).length" class="audit-status-list">
                  <div v-for="entry in questionReviewStatusEntries(audit)" :key="entry.status" class="audit-status-chip">
                    <span class="text-muted">{{ formatQuestionReviewStatus(entry.status) }}:</span>
                    <strong>{{ entry.count }}</strong>
                  </div>
                </div>

                <div class="mt-3 d-flex justify-content-center ">
                  <b-button variant="outline-main" size="sm" class="w-50 mr-1" @click="goToAuditQuestions(audit)">
                    {{ $t("firm.view-question-set") }}
                  </b-button>
                  <b-button variant="outline-primary" size="sm" class="w-50 " @click="goToFirmPolicy(audit)"> {{ $t("audit.view-policies") }} </b-button>
                </div>
              </div>
            </div>
          </div>

          <b-pagination
            v-if="auditsPagination && auditsPagination.docs && auditsPagination.docs.length > 0"
            @click.native.stop
            v-model="request.options.page"
            :total-rows="auditsPagination.totalDocs"
            :per-page="auditsPagination.limit"
            size="sm"
          ></b-pagination>
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<script>
import { getFirmStatus } from "@/helpers/filters";
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";
import FirmStatus from "@cc/firm-status";
import Simplebar from "simplebar-vue";
import { required } from "vuelidate/lib/validators";

export default {
  components: { ContentLoading, Empty, FirmStatus, Simplebar },
  props: {
    firmId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      request: {
        options: { page: 1, limit: 10, sort: { createdAt: "desc" } },
        query: { status: { value: "ACTIVE", isObject: true } },
      },
      tabIndex: 0,
      nameFilter: undefined,
      newAuditName: "",
      isCreatingAudit: false,
      editAuditName: "",
      editAuditStatus: "",
      isUpdatingAudit: false,
      isDeletingAudit: false,
      isInitializing: true, // Loading state for deep link hydration
    };
  },
  async mounted() {
    // Deep link support: fetch firm if not in store
    try {
      // Fetch firm if not loaded OR if loaded firm ID differs from URL param
      if (this.firmId && (!this.firm || this.firm._id !== this.firmId)) {
        await this.$store.dispatch("organizationFirm/findById", this.firmId);
      }
    } catch (error) {
      this.$handleHttpErrorByStatusCode(error);
    } finally {
      this.isInitializing = false;
    }
  },
  watch: {
    nameFilter(n, o) {
      this.request.query = {
        ...this.request.query,
        name: { value: n },
      };
    },
    tabIndex: {
      deep: true,
      handler(v, o) {
        if (v !== o) {
          this.request.options.page = 1;
          this.request.query = { ...this.request.query, status: { value: v === 0 ? "ACTIVE" : "COMPLETED", isObject: true } };
        }
      },
    },
    firmId: {
      immediate: true,
      handler() {
        this.request.options.page = 1;
        this.selectedAudit = undefined;
        this.loadAudits();
      },
    },
    audits: {
      immediate: true,
      handler(list) {
        if ((!this.selectedAudit || !list?.length) && list && list.length) {
          this.selectAudit(list[0]);
        }
      },
    },

    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounceMixin(() => this.loadAudits());
      },
    },
  },
  validations: {
    newAuditName: { required },
    editAuditName: { required },
    editAuditStatus: { required },
  },
  computed: {
    auditsPagination() {
      return this.$store.getters["firmAudit/audits"];
    },
    audits() {
      return this.auditsPagination?.docs ?? [];
    },
    isAuditsLoading() {
      return this.$store.getters["firmAudit/isAuditsLoading"];
    },
    selectedAudit: {
      get() {
        return this.$store.getters["firmAudit/selectedEntity"];
      },
      set(value) {
        this.$store.commit("firmAudit/selectedEntity", value);
      },
    },
    firm() {
      return this.$store.getters["organizationFirm/selectedEntity"];
    },
    currentProduct() {
      return this.$store.getters["product/currentProduct"] || "FWSED";
    },
    firmStatus() {
      return getFirmStatus(this.firm, this.currentProduct);
    },
    statusOptions() {
      return [
        { value: "ACTIVE", text: this.formatAuditStatus("ACTIVE") },
        { value: "COMPLETED", text: this.formatAuditStatus("COMPLETED") },
      ];
    },
  },

  methods: {
    goBack() {
      this.$router.push(`/fwsed/firm-management`);
    },
    goToFirmPolicy(audit) {
      this.selectedAudit = audit;

      this.$router.push(`/fwsed/firm-management/${this.firm._id}/audit/${audit._id}/control`);
    },
    goToAuditQuestions(audit) {
      this.selectedAudit = audit;
      this.$router.push(`/fwsed/firm-management/${this.firm._id}/audit/${audit._id}/question-sets`);
    },

    async loadAudits() {
      if (!this.firmId) {
        this.$store.commit("firmAudit/audits", undefined);
        this.selectedAudit = undefined;

        return;
      }
      try {
        await this.$store.dispatch("firmAudit/fetchAudits", {
          firmId: this.firmId,
          pagination: this.request,
        });
        if (!this.audits || !this.audits.length) {
          this.selectedAudit = undefined;

          return;
        }
        if (!this.selectedAudit && this.audits && this.audits.length) {
          this.selectAudit(this.audits[0]);
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    selectAudit(audit) {
      this.selectedAudit = audit;
    },
    async deleteById(item) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`organizationQuestionSet/delete`, item._id);
        this.rightPanelClosed();

        this.$toast.success("'" + this.$options.filters.translate(item.name) + "' " + this.$t("common.delete-successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { timeout: 15000 });
      }
    },

    refresh() {
      this.loadAudits();
    },

    async createAudit() {
      if (!this.newAuditName || !this.newAuditName.trim()) {
        this.$toast.warning(this.$t("firm.audit.enter-name"));
        return;
      }
      this.isCreatingAudit = true;
      try {
        const response = await this.$store.dispatch("firmAudit/createAudit", {
          firmId: this.firmId,
          name: this.newAuditName.trim(),
          pagination: this.request,
        });
        const created = response?.data;
        this.selectedAudit = created;
        this.$bvModal.hide("create-audit-modal");
        this.newAuditName = "";
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isCreatingAudit = false;
      }
    },
    openEditModal(audit) {
      this.selectedAudit = audit;
      this.editAuditName = this.$options.filters.translate(this.selectedAudit.name) || "";
      this.editAuditStatus = this.selectedAudit.status || "ACTIVE";
      this.$bvModal.show("edit-audit-modal");
    },
    async updateAudit() {
      if (!this.editAuditName || !this.editAuditName.trim()) {
        this.$toast.warning(this.$t("firm.audit.enter-name"));
        return;
      }
      this.isUpdatingAudit = true;
      try {
        const response = await this.$store.dispatch("firmAudit/updateAudit", {
          firmId: this.firmId,
          auditId: this.selectedAudit?._id,
          name: this.editAuditName.trim(),
          status: this.editAuditStatus,
          pagination: this.request,
        });
        const updated = response?.data;
        if (updated) {
          this.selectedAudit = updated;
        }
        this.$bvModal.hide("edit-audit-modal");

        await this.refresh();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isUpdatingAudit = false;
      }
    },
    async confirmDeleteAudit(audit) {
      this.selectedAudit = audit;
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) return;
      await this.deleteAudit(audit);
    },
    async deleteAudit(audit) {
      this.isDeletingAudit = true;
      try {
        await this.$store.dispatch("firmAudit/deleteAudit", { firmId: this.firmId, auditId: audit._id, pagination: this.request });
        this.selectedAudit = undefined;
        await this.loadAudits();
        this.$toast.success(this.$t("common.delete-successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.isDeletingAudit = false;
      }
    },
    formatStatus(status) {
      if (!status) {
        return "-";
      }
      const map = {
        FIRM_REVIEW: this.$t("firm.audit.status.firm-review"),
        BT_REVIEW: this.$t("firm.audit.status.bt-review"),
        COMPLETED: this.$t("firm.audit.status.completed"),
        IN_PROGRESS: this.$t("firm.audit.status.in-progress"),
        REJECTED: this.$t("firm.audit.status.rejected"),
        NONE: this.$t("firm.audit.status.none"),
      };
      return map[status] ?? status;
    },
    formatAuditStatus(status) {
      const map = {
        ACTIVE: this.$t("common.active") || "Aktif",
        COMPLETED: this.$t("firm.audit.status.completed") || "Tamamlandı",
      };
      return map[status] || status || "-";
    },
    formatQuestionSetStatus(status) {
      const map = {
        FIRM_REVIEW: this.$t("firm.audit.status.firm-review"),
        BT_REVIEW: this.$t("firm.audit.status.bt-review"),
        COMPLETED: this.$t("firm.audit.status.completed"),
        IN_PROGRESS: this.$t("firm.audit.status.in-progress"),
        REJECTED: this.$t("firm.audit.status.rejected"),
        NONE: this.$t("firm.audit.status.none"),
      };
      return map[status] || status || "-";
    },
    formatQuestionReviewStatus(status) {
      const map = {
        NONE: this.$t("firm.audit.review.status.none"),
        PENDING: this.$t("firm.audit.review.status.pending"),
        APPROVED: this.$t("firm.audit.review.status.approved"),
        REVISION_REQUIRED: this.$t("firm.audit.review.status.revision"),
      };
      return map[status] || status || "-";
    },
    questionSetStatusEntries(audit) {
      const statusCounts = audit?.questionSetStats?.statusCounts;
      if (!statusCounts) return [];
      return Object.entries(statusCounts)
        .filter(([, count]) => typeof count === "number" && count > 0)
        .map(([status, count]) => ({ status, count }));
    },
    questionReviewStatusEntries(audit) {
      const statusCounts = audit?.questionReviewStats?.statusCounts;

      if (!statusCounts) return [];
      return Object.entries(statusCounts)
        .filter(([, count]) => typeof count === "number")
        .map(([status, count]) => ({ status, count }));
    },
    getQuestionReviewTotal(audit) {
      return (audit && audit.questionReviewStats && audit.questionReviewStats.total) || 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.audit-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.audit-card {
  flex: 1 1 300px;
  max-width: 400px;
  border: 1px solid #e6e9ef;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;

  &:hover {
    border-color: #4b72fa;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &.active {
    border-color: #4b72fa;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
  }

  .audit-name {
    font-weight: 600;
    margin-bottom: 6px;
  }

  .audit-meta {
    font-size: 12px;
    color: #6c757d;
    display: flex;
    justify-content: space-between;
  }
}

.audit-status-list {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin-top: 4px;
  overflow-x: auto;
}

.audit-status-chip {
  background: #f7f8fb;
  border: 1px solid #e6e9ef;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 0px;
}
</style>
